import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { api, rows } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { Badge } from "@/components/ui/badge";
import { activityStatusLabel } from "@/lib/requestKinds";
import { businessDay } from "@/lib/format";

// Items that have merely been sent and nothing has happened yet — everything else counts as
// "actively progressing" for rung 2. Mirrors the mobile app's ResumeStrip ladder so both platforms
// pick the same "most relevant" item the same way.
const PASSIVE_STATUSES = ["requested", "matching"];

function rank(items) {
  const today = businessDay();
  const isToday = (i) => i.happens_at && businessDay(i.happens_at) === today;
  const rung1 = items.filter((i) => isToday(i));
  const rung2 = items.filter((i) => !isToday(i) && !PASSIVE_STATUSES.includes(i.status));
  const rung3 = items.filter((i) => !isToday(i) && PASSIVE_STATUSES.includes(i.status));
  return [...rung1, ...rung2, ...rung3];
}

/** One most-relevant open item from the unified activity spine, signed-in only. Renders nothing
 * signed out, still loading, or with no open items — the offer-of-day card fills the row instead. */
export function ResumeStrip() {
  const { t, lang, tenant, auth } = useApp();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!auth || !tenant) {
      setItems(null);
      return;
    }
    api.get("/activity/mine", { params: { tenant_id: tenant.id, group: "open", limit: 100 } }).then((r) => setItems(rows(r.data)));
  }, [auth, tenant]);

  if (!auth || !items || items.length === 0) return null;

  const ordered = rank(items);
  const top = ordered[0];
  const moreCount = ordered.length - 1;

  return (
    <Link
      to="/activity"
      data-testid="home-resume-strip"
      className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 transition-transform duration-200 hover:-translate-y-0.5"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
        <Clock size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold">{localized(lang, top, "title")}</span>
        <span className="mt-1 flex items-center gap-2">
          <Badge className="bg-[hsl(var(--accent))]" data-testid="home-resume-strip-status">
            {activityStatusLabel(t, top)}
          </Badge>
          {moreCount > 0 && (
            <span className="text-xs text-muted-foreground">{t("home.resumeMore", { count: moreCount })}</span>
          )}
        </span>
      </span>
      <ArrowRight size={16} className="shrink-0 text-muted-foreground" />
    </Link>
  );
}
